import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core'
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule, type MatCheckboxChange } from '@angular/material/checkbox'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatRadioModule, type MatRadioChange } from '@angular/material/radio'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { AccountObject, DealPartyE, DealViewRawBids, DealViewRawCosts, DealViewRawDeal, DealViewRawFiles, DealViewRawInvoices, DealViewRawOffers, DealViewRawSegments, PaymentTermsDates, paymentTermsDatesForAccountType, type DealViewRawStatus, type Formula, type FormulaLine, type PaymentTermsDate } from '@tradecafe/types/core'
import { DeepPartial, DeepReadonly, upgradeDealFormula } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { compact, isEqual, isNil, uniq, uniqWith } from 'lodash-es'
import { BehaviorSubject, combineLatest, of } from 'rxjs'
import { map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { selectAccountEntities, selectBuyers, selectSuppliers } from 'src/app/store/accounts'
import { massDealForm, massDealFormFailure, massDealFormSuccess } from 'src/app/store/deal-view.actions'
import { loadFormulaValues, selectDefaultPriceSource } from 'src/app/store/formula-values'
import { selectAllLocations } from 'src/app/store/locations'
import { selectAllMeasures } from 'src/app/store/measures'
import { selectAllPricingTerms } from 'src/app/store/pricing-terms'
import { selectProductEntities } from 'src/app/store/products'
import { selectAllUsers } from 'src/app/store/users'
import { FormulaPricingApiService, type FormulaPricingPreviewFormula } from 'src/api/formula-pricing'
import { formulaPricingLineEntry, isUsdaProposalOnDealEnabled, type FormulaPricing, type FormulaPricingLineEntry } from 'src/components/deals-list/formula-pricing'
import { usdaLivePreview$, usdaPanelModel, UsdaProposalPanelComponent, type UsdaPreviewState } from 'src/components/usda-proposal-panel'
import { ReactiveAsteriskModule } from 'src/components/reactive-asterisk/reactive-asterisk.module'
import { SelectSearchModule } from 'src/components/select-search/select-search.module'
import { canChangeParty } from 'src/services/data/deal-view-permissions.service'
import { waitNotEmpty } from 'src/services/data/utils'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { isUsdaPriceSource, isUsdaProductNameEnabled, priceSourceOptions$, searchUsdaProductNameOption, UsdaFormulaService, type UsdaProductNameState } from 'src/shared/formula-form'
import { replayForm } from 'src/shared/utils/replay-form'
import { DealFormDto } from '../deal-form.schema'
import { ChangePartyService } from './change-party.service'

interface ChangeFormulaOptions {
  change: 'formula'
  productIndex: number
}
interface ChangePartyOptions {
  change: 'party'
}
export type ChangePartyFormOptions = {
  /**
   * Deal Views, all have same set of products:
   *    1. in every deal bid[i] corresponds to offer[i]
   *    2. all deals have same amount of products (bids and offers)
   *    3. products are ordered the same way in all deals (meanin product[i] in one deal is the same product[i] in another deal)
   */
  deals: DeepReadonly<Array<DealViewRawDeal & DealViewRawOffers & DealViewRawBids & DealViewRawCosts & DealViewRawFiles & DealViewRawSegments & DealViewRawInvoices & DealViewRawStatus>>
  party: undefined | DealPartyE.buyer | DealPartyE.supplier
} & (ChangePartyOptions | ChangeFormulaOptions)

export type ChangePartyFormResult = DeepPartial<DealFormDto>[]

@Component({
    selector: 'tc-change-party-form',
    templateUrl: './change-party-form.component.html',
    styleUrls: ['./change-party-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatExpansionModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressBarModule,
        MatRadioModule,
        ReactiveAsteriskModule,
        SelectSearchModule,
        MatSlideToggleModule,
        UsdaProposalPanelComponent,
    ]
})
export class ChangePartyFormComponent extends OnDestroyMixin implements OnInit {
  private readonly actions$ = inject(Actions)
  private readonly AuthApi = inject(AuthApiService)
  private readonly ChangeParty = inject(ChangePartyService)
  private readonly dialogData = inject<ChangePartyFormOptions>(MAT_DIALOG_DATA)
  private readonly dialogRef = inject<MatDialogRef<ChangePartyFormComponent, ChangePartyFormResult>>(MatDialogRef)
  private readonly store = inject(Store)
  private readonly toaster = inject(ToasterService)
  private readonly UsdaFormula = inject(UsdaFormulaService)
  private readonly FormulaPricingApi = inject(FormulaPricingApiService)


  // static / config
  private readonly deals = this.dialogData.deals

  protected readonly party = this.dialogData.party
  protected readonly editableParty = this.dialogData.change === 'party'

  // internal state
  protected readonly inProgress$ = new BehaviorSubject<'save' | undefined>(undefined)
  protected readonly allVisible$ = new BehaviorSubject<Record<string, boolean>>({})
  private readonly initial: { account?: string } = {}

  // editable form
  protected readonly form = new FormGroup({
    account: new FormControl<number>(undefined, Validators.required), // this.account
    dontChangePrice: new FormControl<boolean>(false),
  })

  // ref data
  private readonly products$ = this.store.pipe(select(selectProductEntities), waitNotEmpty(), shareReplay(1))

  protected readonly productLines = this.dialogData.deals[0].bids.map((bid, i) => {
    const offer = bid.offer
      ? this.dialogData.deals[0].offers.find(o => bid.offer && o.offer_id === bid.offer)
      : this.dialogData.deals[0].offers[i]
    const product = offer.product
    const form = this.buildFormulaForm()
    const usda = this.buildUsdaLine(i, bid, offer)

    return {
      i,
      show: this.dialogData.change === 'party' ? true : i === this.dialogData.productIndex,
      bid,
      offer,
      title$: this.products$.pipe(map(products => products[product]?.name)),
      type_id$: this.products$.pipe(map(products => products[product]?.type_id)),
      form,
      linesForm: form.controls.lines,
      priceSourceOptions$: priceSourceOptions$(this.store, form.controls.priceSource),
      // USDA formulas pick the product name from the USDA catalogue (WA-21919)
      usdaProductName$: new BehaviorSubject<UsdaProductNameState>({ applies: false, options: [] }),
      initialFormula: false,
      // WA-22777: USDA proposal for this line (single deal, flag on); null = no panel
      usda,
      // a saved price (0 included) is the trader's; switching to flat never replaces it with the proposal
      hasSavedPrice: !isNil(this.party === DealPartyE.buyer ? bid.price : offer.price),
    }
  })

  // ref data
  protected readonly accounts$ = this.party === 'buyer'
    ? this.store.pipe(select(selectBuyers()), waitNotEmpty())
    : this.store.pipe(select(selectSuppliers()), waitNotEmpty())
  private readonly accountsByIds$ = this.store.pipe(select(selectAccountEntities), waitNotEmpty())
  private readonly locations$ = this.store.pipe(select(selectAllLocations), waitNotEmpty())
  protected readonly pricingTerms$ = this.store.pipe(select(selectAllPricingTerms), waitNotEmpty())
  protected readonly measures$ = this.store.pipe(select(selectAllMeasures), waitNotEmpty())
  protected readonly users$ = this.store.pipe(select(selectAllUsers), waitNotEmpty())
  protected paymentTermsDates: PaymentTermsDates;
  protected readonly searchUsdaProductNameOption = searchUsdaProductNameOption


  // calculated fields
  protected readonly currency = this.printCurrency()


  ngOnInit() {
    this.store.dispatch(loadFormulaValues())

    // check all deals - can user change party?
    if (this.editableParty) {
      const denied = this.deals.filter(dv => !canChangeParty(dv.deal, this.party, this.AuthApi.currentUser))
      if (denied.length) {
        this.toaster.warning(`Deal${denied.length === 1 ? ` ${denied[0].deal.deal_id} is` : 's are'} submitted. Can't change ${this.party}.`)
        this.form.controls.account.disable()
      }
    }

    const isB = this.party === DealPartyE.buyer
    // if all deals have the same party - preselect "account" form field
    const accountIds = uniq(compact(this.deals.map(dv => isB ? dv.deal.buyer_id : dv.deal.supplier_id)))
    if (accountIds.length === 1) {
      this.form.controls.account.setValue(parseFloat(accountIds[0]))
      this.initial.account = accountIds[0]
    }

    combineLatest([
      replayForm(this.form.controls.account),
      this.accountsByIds$,
    ]).pipe(
      untilComponentDestroyed(this),
      map(([customer, accounts]) => accounts[customer]),
      waitNotEmpty(),
    ).subscribe((account) => {
      this.paymentTermsDates = paymentTermsDatesForAccountType(account.type)
    });

    // if all deals have the same fixed price - preselect "price" form field
    this.productLines.forEach((pl, i) => {
      this.UsdaFormula.productName$(of(pl.offer.product), pl.form.controls.priceSource, pl.form.controls.productName).pipe(
        untilComponentDestroyed(this),
      ).subscribe(pl.usdaProductName$)

      if (!this.editableParty) {
        pl.form.controls.checked.disable()
      }

      const prices = uniq(compact(this.deals.map(dv => isB ? dv.bids[i].price : dv.offers[i].price)))
      if (prices.length === 1) {
        pl.form.controls.price.setValue(prices[0])
      }

      // if all deals have the same formula - preselect "overridePrice" radio button
      const partyFormulas = compact(this.deals.map(({deal, bids, offers}) => isB
        ? bids[i].formula || !i && upgradeDealFormula(deal.attributes.buyer_formula, bids[i].incoterm)
        : offers[i].formula || !i && upgradeDealFormula(deal.attributes.supplier_formula, offers[i].incoterm)))
      const formulas = uniqWith(partyFormulas, isEqual)
      if (formulas.length === 1) {
        pl.form.controls.priceType.setValue('formula')
        pl.initialFormula = !!formulas[0]
      } else if (pl.form.controls.price.value && !formulas.length) {
        pl.form.controls.priceType.setValue('flat')
      }

      // preselect product section
      if (this.dialogData.change === 'party' ? pl.form.controls.price.value : this.dialogData.productIndex === i) {
        pl.form.controls.checked.setValue(true)
      }

      // preselect 'flat' by default when user is changing formula on a fresh deal
      if (!accountIds.length && this.dialogData.change === 'formula' && this.dialogData.productIndex === i && !pl.form.controls.priceType.value) {
        pl.form.controls.priceType.setValue('flat')
      }

      // prefill formula form if all deals have same formula[i]
      if (formulas.length < 2) {
        if (formulas[0]) {
          this.patchFormWithFormula(pl, formulas[0])
        } else {
          this.accountsByIds$.pipe(take(1)).subscribe(async accounts => {
            this.patchFormWithFormula(pl, await this.defaultFormula(accounts[this.form.controls.account.value], pl))
          })
        }
      }

      // WA-22777: the formula on screen; the panel compares the nightly entry against it (not the saved one)
      if (pl.usda) {
        replayForm(pl.form).pipe(map(() => this.readFormulaFromForm(pl)), untilComponentDestroyed(this))
          .subscribe(formula => pl.usda.currentFormula$.next(formula))
      }

      // WA-22777: live USDA price preview of the formula on screen (deal saved, line shown)
      const dealId = this.deals[0].deal.deal_id
      if (pl.usda && dealId && pl.show) {
        usdaLivePreview$({
          formula$: pl.usda.currentFormula$.asObservable(),
          preview: formula => this.FormulaPricingApi.preview(dealId, {
            side: pl.usda.side,
            product_index: pl.i,
            formula: formula as FormulaPricingPreviewFormula,
          }),
          nightly: pl.usda.entry,
          visible: pl.usda.savedUsda,
        }).pipe(untilComponentDestroyed(this)).subscribe(state => pl.usda.preview$.next(state))
      }

      // WA-22777: switching to flat with no price prefills the USDA proposal (never over a saved price)
      if (pl.usda && !pl.hasSavedPrice) {
        pl.form.controls.priceType.valueChanges.pipe(untilComponentDestroyed(this)).subscribe(priceType => {
          const price = pl.form.controls.price.value
          const model = this.usdaModel(pl)
          if (priceType === 'flat' && model?.canPrefill && (price === undefined || price === null || (price as unknown) === '')) {
            pl.form.controls.price.setValue(model.view.price)
          }
        })
      }
    })
  }

  /** WA-22777: "Use proposed price" – the trader confirms it with the existing flat price save */
  protected useProposedPrice(pl: typeof this.productLines[number]) {
    const model = this.usdaModel(pl)
    if (!model?.canPrefill) return
    pl.form.controls.priceType.setValue('flat')
    pl.form.controls.price.setValue(model.view.price)
    pl.form.controls.price.markAsDirty()
  }

  private usdaModel(pl: typeof this.productLines[number]) {
    const u = pl.usda
    if (!u) return null
    const preview = u.preview$.value
    if (!(preview ? preview.visible : u.savedUsda)) return null
    return usdaPanelModel({ ...u, formula: u.currentFormula$.value, preview })
  }

  /** WA-22777: USDA proposal context for a product line, or null when the panel never applies */
  private buildUsdaLine(
    i: number,
    bid: ChangePartyFormOptions['deals'][number]['bids'][number],
    offer: ChangePartyFormOptions['deals'][number]['offers'][number],
  ) {
    if (!isUsdaProposalOnDealEnabled() || this.deals.length !== 1 || !this.party) return null
    const isB = this.party === DealPartyE.buyer
    const { deal } = this.deals[0]
    const formula = isB
      ? bid?.formula || !i && upgradeDealFormula(deal.attributes?.buyer_formula, bid?.incoterm)
      : offer?.formula || !i && upgradeDealFormula(deal.attributes?.supplier_formula, offer?.incoterm)
    const formulaPricing = (deal as { formula_pricing?: FormulaPricing }).formula_pricing
    const side: 'buyer' | 'supplier' = isB ? 'buyer' : 'supplier'
    return {
      side,
      entry: formulaPricingLineEntry(formulaPricing, side, isB ? bid?.bid_id : offer?.offer_id) as FormulaPricingLineEntry | null,
      /** an unsaved deal has nothing for the nightly run to evaluate */
      evaluated: !!deal.deal_id,
      savedFormula: formula || null,
      savedUsda: !!formula && isUsdaPriceSource(formula.price_source),
      currency: isB ? bid?.currency : offer?.currency,
      unit: isB ? bid?.weight?.unit : offer?.weight?.unit,
      preview$: new BehaviorSubject<UsdaPreviewState | null>(null),
      /** the formula on screen (starts as the saved one) */
      currentFormula$: new BehaviorSubject<DeepReadonly<Formula> | null>(formula || null),
    }
  }

  private readFormulaFromForm(pl: typeof this.productLines[number]): Formula {
    const form = pl.form.value // skip disabled fields
    return {
      product_name: form.productName,
      price_source: form.priceSource,
      days: form.days,
      before_date: form.beforeDate,
      lines: pl.linesForm.getRawValue().map(({ positive, ...line}) => ({
        ...line,
        price: positive ? line.price : -line.price,
      })),
    }
  }

  protected save() {
    this.form.markAllAsTouched()
    this.form.updateValueAndValidity()

    this.productLines.forEach(pl => {
      pl.linesForm.markAllAsTouched();
      pl.linesForm.updateValueAndValidity()
    });

    const { account, dontChangePrice } = this.form.value

    const areFormulasValid = this.productLines
      .filter(pl => pl.form.controls.priceType.value === 'formula')
      .every(pl => pl.linesForm.valid);

    if (!areFormulasValid) {
      this.toaster.warning('Please fix all errors before saving.')

      return;
    }

    // USDA formulas must name a USDA catalogue item
    const invalidUsdaProductNames = this.productLines
      .filter(pl => pl.form.controls.checked.value && pl.form.controls.priceType.value === 'formula')
      .filter(pl => pl.form.controls.productName.hasError('usdaProductName'))
    if (invalidUsdaProductNames.length) {
      invalidUsdaProductNames.forEach(pl => pl.form.controls.productName.markAsTouched())
      this.toaster.warning('Please pick the Product Name from the USDA list.')

      return;
    }

    this.inProgress$.next('save')
    const isB = this.party === DealPartyE.buyer
    combineLatest(this.deals.map((dv) => {
      if (!account || account.toString() === dv.deal[`${this.party}_id`]) {
        return of({ dv, patch: { details: { deal_id: dv.deal.deal_id }} as DeepPartial<DealFormDto>})
      } else {
        return this.ChangeParty.setDealParty(dv, this.party, account.toString())
          .pipe(map(patch => ({ dv, patch })))
      }
    })).pipe(
      map(pairs => pairs.map(({ dv, patch }) => {
        if (!dontChangePrice) {
          let products = patch.products || dv.bids.map(({bid_id, offer}) => ({ bid_id, offer_id: offer }))
          products = products.map((pp, i) => {
            const pl = this.productLines[i]
            const { checked, priceType, price } = pl.form.getRawValue()
            if (!checked) return pp
            if (priceType === 'formula') {
              const formula = this.readFormulaFromForm(pl)
              return isB
                ? { ...pp, buyerFormula: formula }
                : { ...pp, supplierFormula: formula }
            } else if (priceType === 'flat') {
              return isB
                ? { ...pp, buyerEstPrice: price, buyerActualPrice: price, buyerFormula: null }
                : { ...pp, supplierEstPrice: price, supplierActualPrice: price, supplierFormula: null }
            }
            return pp // should never reach this line
          })
          patch = { ...patch, products }
        }
        return {dv, patch}
      })),
      tap(changes => {
        const result = changes.map(c => c.patch)
        if (this.dialogData.deals[0].deal.deal_id) {
          this.store.dispatch(massDealForm({ party: this.party, changes }))
          this.actions$.pipe(ofType(massDealFormSuccess, massDealFormFailure), take(1)).subscribe((action) => {
            if (action.type === massDealFormSuccess.type) this.dialogRef.close(result)
            this.inProgress$.next(undefined)
          })
        } else {
          this.dialogRef.close(result)
        }
      }))
    .subscribe()
  }

  private printCurrency() {
    const isB = this.party === DealPartyE.buyer
    const currencies = uniq(this.deals.map(dv => isB ? dv.bids[0].currency : dv.offers[0].currency))
    let currency = ''
    if (currencies.length === 1) [currency] = currencies
    return currency
  }

  protected filteredLocations$(productIndex: number, lineIndex: number) {
    return combineLatest([
      this.allVisible$, this.accountsByIds$, this.locations$,
      replayForm(this.productLines[productIndex].linesForm).pipe(map(lines => lines[lineIndex])),
    ]).pipe(map(([allVisible, accounts, locations, lineForm]) => {
      const key = `${productIndex}-${lineIndex}`
      if (allVisible[key]) return locations
      const account = accounts[this.form.controls.account.value]
      const visibleIds = account?.attributes?.locations || []
      return locations.filter(l => visibleIds.includes(l.location_id) || l.location_id === lineForm.location)
    }))
  }

  protected toggleLocations(productIndex: number, lineIndex: number) {
    const key = `${productIndex}-${lineIndex}`
    this.allVisible$.next({
      ...this.allVisible$.value,
      [key]: !this.allVisible$.value[key],
    })
  }

  protected addNewLine(pl: typeof this.productLines[number], line?: DeepReadonly<FormulaLine>) {
    pl.linesForm.push(this.buildLineForm(line))
  }

  protected removeLine(pl: typeof this.productLines[number], lineIndex: number) {
    pl.linesForm.removeAt(lineIndex)
  }

  protected onAccountChange(account: DeepReadonly<AccountObject>) {
    if (this.initial.account !== account.account.toString()) {
      this.productLines.forEach(async pl => {
        if (!pl.initialFormula) {
          this.patchFormWithFormula(pl, await this.defaultFormula(account, pl))
        }
      })
    }
  }

  protected onDontChangePriceChange($event: MatRadioChange) {
    if ($event.value) {
      this.productLines.forEach(pl => {
        pl.form.controls.checked.setValue(false)
      })
    }
  }

  protected onCheckedChange($event: MatCheckboxChange) {
    if ($event.checked) {
      this.form.controls.dontChangePrice.reset()
    }
  }

  private buildFormulaForm() {
    return new FormGroup({
      // component internal fields
      checked: new FormControl<boolean>(false),
      priceType: new FormControl<'flat'|'formula'|undefined>(undefined),
      // business fields
      price: new FormControl<number>(undefined, [Validators.required, Validators.min(0)]), // this.price
      productName: new FormControl<string>(undefined, Validators.required), // this.formula.product_name
      priceSource: new FormControl<string>(undefined, Validators.required), // this.formula.price_source
      days: new FormControl<number>(undefined, [Validators.required, Validators.min(0)]), // this.formula.days_before_pickup
      beforeDate: new FormControl<PaymentTermsDate>(undefined, Validators.required), // this.formula.before_date
      lines: new FormArray<ReturnType<typeof this.buildLineForm>>([], Validators.required),
    })
  }

  private buildLineForm(line?: DeepReadonly<FormulaLine>) {
    const isPriceDefined = typeof line?.price === 'number'
    const positive = isPriceDefined ? line.price >= 0 : true // yes, 0 is also positive here, sorry.
    const price = isPriceDefined ? Math.abs(line.price) : undefined
    return new FormGroup({
      positive: new FormControl<boolean>(positive, Validators.required),
      price: new FormControl<number>(price, Validators.required),
      location: new FormControl<string>(line?.location, Validators.required),
      incoterm: new FormControl<string>(line?.incoterm, Validators.required),
      conditions: new FormControl<string>(line?.conditions),
    })
  }

  private patchFormWithFormula(pl: typeof this.productLines[number], formula: Formula) {
    pl.form.patchValue({
      productName: formula.product_name,
      priceSource: formula.price_source,
      days: formula.days,
      beforeDate: formula.before_date,
    })
    pl.form.controls.lines.clear()
    formula.lines.forEach(line => this.addNewLine(pl, line))
  }

  private async defaultFormula(account: DeepReadonly<AccountObject>, pl: typeof this.productLines[number]): Promise<Formula> {
    const isB = this.party === DealPartyE.buyer

    const [{deal, bids: [bid], offers: [offer]}] = this.deals
    let location
    if (this.initial.account === account?.account.toString()) {
      location = isB ? bid.attributes.incoterm_location : offer.attributes.incoterm_location;
    }

    location = location || (isB ? deal.dest_location : deal.origin_location);
    location = location || account?.attributes?.pricing?.tax_location;

    let incoterm = isB ? pl.bid.incoterm : pl.offer.incoterm

    const partyFormulas = this.editableParty
      ? compact(this.deals.map(({deal, bids, offers}) =>
          isB
            ? bids[pl.i]?.formula || pl.i === 0 && upgradeDealFormula(deal.attributes.buyer_formula, pl.bid.incoterm)
            : offers[pl.i]?.formula || pl.i === 0 && upgradeDealFormula(deal.attributes.supplier_formula, pl.offer.incoterm)))
      : []
    const oppositeFormulas = this.editableParty
      ? compact(this.deals.map(({deal, bids, offers}) =>
          isB
            ? offers[pl.i]?.formula || pl.i === 0 && upgradeDealFormula(deal.attributes.supplier_formula, pl.offer.incoterm)
            : bids[pl.i]?.formula || pl.i === 0 && upgradeDealFormula(deal.attributes.buyer_formula, pl.bid.incoterm)))
      : compact([isB
        ? pl.offer.formula || pl.i === 0 && upgradeDealFormula(deal.attributes.supplier_formula, pl.offer.incoterm)
        : pl.bid.formula || pl.i === 0 && upgradeDealFormula(deal.attributes.buyer_formula, pl.bid.incoterm)])

    // take default name from any formula within selection
    const formula = partyFormulas[0] || oppositeFormulas[0]
    if (!formula) {
      const [product_name, [price_source, before_date]] = await combineLatest([
        pl.title$,
        pl.type_id$.pipe(switchMap(type_id => combineLatest([
          this.store.pipe(select(selectDefaultPriceSource({
            product_id: isB ? pl.bid.product : pl.offer.product,
            type_id,
            buyer_id: deal.buyer_id,
            supplier_id: deal.supplier_id,
            trader_id: isB ? deal.trader_user_id : deal.trader_user_id_supplier,
          }))),
          // TODO: select selectDefaultBeforeDate when it is ready
          of(undefined as PaymentTermsDate | undefined),
        ]))),
      ]).pipe(take(1)).toPromise();
      return {
        // USDA formulas name a USDA catalogue item, not the TradeCafe product title (WA-21919)
        product_name: isUsdaProductNameEnabled() && isUsdaPriceSource(price_source) ? undefined : product_name,
        price_source,
        before_date,
        lines: [{ location, incoterm }],
      } as Formula
    }
    const { product_name, price_source } = formula

    // take default values from the first selected deal with "opposite party" formula defined
    const oppositeFormula = oppositeFormulas[0]
    if (!oppositeFormula) return { product_name, price_source, lines: [{ location, incoterm }] } as Formula

    return {
      product_name,
      price_source,
      days: oppositeFormula.days,
      before_date: oppositeFormula.before_date,
      lines: [{
        incoterm,
        location,
        price: oppositeFormula.lines[0]?.price,
      }],
    }
  }
}
